import React, { useState, useEffect } from 'react';
import { Linking, Platform, RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native'

import api from "../../../utils/api"
import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import IcomoonIcon from '../../../components/IcomoonIcon';
import { Illustration } from '../../../components/Illustration';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import IconButton from '../../../components/IconButton';
import { useNavigation, useRoute } from '@react-navigation/native';
import Header from '../../../navigation/Header';
import AddPaymentModal from './Fees/Fees/AddPaymentModal';
import NewExpenseModal from './Fees/Expenses/NewExpenseModal';
import NewAnnouncementModal from './Announcements/NewAnnouncementModal';
import useActiveAdminCommunity from '../../../hooks/useActiveAdminCommunity';
import { firstSidebarRoute } from '../../../navigation/web/sidebarStructure';
import useUnitsSearch from '../../../hooks/useUnitsSearch';
import SearchInput from '../../../components/SearchInput';
import { Icon } from '../../../components/Icon';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import NewDocumentModal from './Documents/NewDocumentModal';
import DocumentDetailModal from './Documents/DocumentDetailModal';


function FeeRow({fee, title, amountToCash, unitsAmount}){
  const navigation = useNavigation()
  const route = useRoute()

  function goToFee() {
    navigation.navigate("Admin.Fees.Detail", {
      feeId: fee.uuid,
      communityId: route.params.communityId,
    })
  }

  return (
    <TouchableOpacity style={tw`py-2 border-b border-white/50`} onPress={goToFee}>
      <View style={tw`row justify-between`}>
        <Text style={tw`flex-1 font-600 text-gray-700`}>{title}</Text>

        {/* <Icon name='chevron-right' style={tw`text-gray-700 ml-2`} /> */}
      </View>

      <View style={tw`row justify-between`}>
        <View>
          <Text style={tw`font-600 text-2xl text-secondary`}>{unitsAmount}</Text>
        </View>

        <View style={tw`items-center`}>
          <Text style={tw`text-secondary font-600`}>${amountToCash}</Text>
          <Text style={tw`text-secondary -mt-1.5`}>por cobrar</Text>
        </View>
      </View>
    </TouchableOpacity>
  )
}




function AnnouncementButton({ announcement, onPress }){
  const dateStr = dayjs(announcement.created_at).format('DD/MMM/YYYY')

  return (
    <TouchableOpacity style={tw`bg-white rounded-lg px-4 py-3 mb-3`} onPress={onPress}>

      <Text style={tw`text-gray-800 font-500 text-secondary mb-1`}>{announcement.title}</Text>
      <Text style={tw`text-gray-500`} numberOfLines={2}>{announcement.text}</Text>
      <Text style={tw`text-gray-400 text-right mt-2`}>{dateStr}</Text>
    </TouchableOpacity>
  )
}


export default function DashboardScreen({ navigation, route }) {
  const { communityId, community, can } = useActiveAdminCommunity()
  const canViewDashboard = can('dashboard.view')

  // Resumen es la entrada al panel; sin permiso (p. ej. un rol de monitoreo) se
  // manda a la primera sección que sí puede ver.
  useEffect(() => {
    if (community && !canViewDashboard) {
      navigation.navigate(firstSidebarRoute(community))
    }
  }, [community, canViewDashboard])

  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [showNewAnnouncementModal, setShowNewAnnouncementModal] = useState(false);
  const [selectedAnnouncementModal, setSelectedAnnouncementModal] = useState(null);


  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [selectedDocumentModal, setSelectedDocumentModal] = useState(null);

  // Buscador rápido de domicilios
  const [searchQuery, setSearchQuery] = useState("");

  // Acordeón de carpetas de documentos (funcionalidad futura)
  const [docsFolderOpen, setDocsFolderOpen] = useState(true);

  // Ancho del contenido para decidir columnas en web.
  const [contentWidth, setContentWidth] = useState(0);

  // Panel de detalle (columna 3, web). Genérico: { type, data }.
  // Hoy soporta 'announcement'; a futuro: 'vote', 'reservation', etc.
  const [detailPanel, setDetailPanel] = useState(null);


  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ['admin.community.dashboard', communityId],
    queryFn: () => api.get(`/communities/${communityId}/admin/dashboard`).then(response => response.data),
    enabled: canViewDashboard,
  })

  // Coincidencias del buscador de domicilios
  const { filteredUnits, isSearching } = useUnitsSearch({ communityId, searchQuery })

  // Buscador rápido: solo mostramos los primeros N para no pintar miles de filas
  // (un fraccionamiento puede tener ~10k viviendas) en un map sin virtualizar.
  const MAX_SEARCH_RESULTS = 20
  const searchResults = filteredUnits?.slice(0, MAX_SEARCH_RESULTS) ?? []
  const extraResultsCount = (filteredUnits?.length ?? 0) - searchResults.length



  // Sin permiso el query queda deshabilitado (data undefined); el efecto de arriba redirige.
  if (!canViewDashboard) return null

  if (isLoading) return <Text style={tw`text-lg text-gray-600 mt-40 text-center`}>Cargando...</Text>

  if (error) return <Text>Ocurrió un error: {error.message}</Text>


  // Fecha de hoy en español, con mayúscula inicial: "Jueves 24 de junio 2026"
  const todayLong = dayjs().locale('es').format('dddd D [de] MMMM YYYY')
  const todayLabel = todayLong.charAt(0).toUpperCase() + todayLong.slice(1)

  // Abre el perfil de la vivienda (igual que la sección "Viviendas")
  function goToUnitDetail(unit) {
    navigation.navigate("Admin.Units.Detail", {
      communityId,
      unitId: unit.uuid,
    })
  }

  // Centro de pendientes. El backend solo incluye el contador (dataKey) si la comunidad
  // tiene activada esa feature; por eso filtramos las filas a las que sí vienen en data.pending.
  const pendingItems = [
    {
      key: 'reports',
      dataKey: 'reports_in_progress',
      title: 'Reportes',
      subtitle: 'Abiertos',
      icon: <Icon name="navIncidencias" size={22} />,
      highlight: true,
      onPress: () => navigation.navigate('Admin.Tickets.Index'),
    },
    {
      key: 'payment_evidences',
      dataKey: 'payment_evidences',
      title: 'Comprobantes de pago',
      icon: <Icon name="navFinanzas" size={22} />,
      onPress: () => navigation.navigate('Admin.Payments.Evidences', { communityId }),
    },
    {
      key: 'access_authorizations',
      dataKey: 'access_authorizations',
      title: 'Autorizaciones de acceso',
      icon: <Icon name="navAccesos" size={22} />,
      // Admin.AccessAuthorizations.Index solo existe en el stack web; en nativo no navega.
      onPress: () => { if (Platform.OS === 'web') navigation.navigate('Admin.AccessAuthorizations.Index') },
    },
  ]
    .filter(item => data.pending?.[item.dataKey] !== undefined)
    .map(item => ({ ...item, count: data.pending[item.dataKey] }))

  const totalPending = pendingItems.reduce((sum, it) => sum + (it.count ?? 0), 0)

  const money = (n) => `$${Number(n || 0).toLocaleString('es-MX')}`

  // Ingresos y gastos del mes vienen ya calculados en /dashboard.
  const income = {
    maintenanceCollected: data.income?.maintenance?.collected ?? 0,
    maintenanceRemainingAmount: data.income?.maintenance?.remaining_amount ?? 0,
    maintenanceRemainingCount: data.income?.maintenance?.remaining_count ?? 0,
    extraordinaryCount: data.income?.extraordinary?.count ?? 0,
    extraordinaryAmount: data.income?.extraordinary?.amount ?? 0,
  }
  const incomeTotal = income.maintenanceCollected + income.maintenanceRemainingAmount
  const incomeProgress = incomeTotal > 0 ? Math.round((income.maintenanceCollected / incomeTotal) * 100) : 0

  const expenses = {
    total: data.expenses?.total ?? 0,
    count: data.expenses?.count ?? 0,
  }

  // Comunicación: último comunicado subido.
  const lastAnnouncement = data.announcements?.[0]

  // Relativo simple para "ÚLTIMO AVISO · ...": HOY / AYER / HACE N DÍAS
  function relativeDateLabel(dateStr) {
    const days = dayjs().startOf('day').diff(dayjs(dateStr).startOf('day'), 'day')
    if (days <= 0) return 'HOY'
    if (days === 1) return 'AYER'
    return `HACE ${days} DÍAS`
  }

  // Alcance del comunicado: vistos sobre nº de viviendas (del endpoint /dashboard).
  const reachSeen = lastAnnouncement?.seen_count ?? 0
  const reachTotal = lastAnnouncement?.recipients_count ?? 0
  const hasReach = reachTotal > 0
  const reachPct = hasReach ? Math.round((reachSeen / reachTotal) * 100) : 0

  // Carpeta de documentos de muestra (funcionalidad futura de carpetas)
  const sampleFolder = {
    name: 'Actas de Asamblea',
    docs: ['Asamblea_mayo2026.pdf', 'Asamblea_abril2026.pdf'],
  }


  // Dos columnas en web cuando hay ancho suficiente (Resumen fullscreen).
  const isWeb = Platform.OS === 'web'
  const multiColumn = isWeb && contentWidth >= 600
  const threeColumns = isWeb && contentWidth >= 1050

  const buscadorSection = (
    <View style={tw`mb-4`}>
      <SearchInput
        placeholder="Buscar domicilio..."
        onChange={setSearchQuery}
        value={searchQuery}
        style={{ input: tw`bg-white border border-gray-200 py-3` }}
      />
      {
        isSearching &&
          <View style={tw`bg-white rounded-2xl border border-gray-200 mt-3 overflow-hidden`}>
            {
              searchResults.length === 0
                ? <Text style={tw`text-gray-400 text-center py-4`}>Sin coincidencias</Text>
                : searchResults.map((unit, index) =>
                    <TouchableOpacity
                      key={unit.uuid}
                      style={tw.style(`row justify-between px-4 py-3`, index > 0 && `border-t border-gray-100`)}
                      onPress={() => { setSearchQuery(""); goToUnitDetail(unit) }}
                    >
                      <View style={tw`flex-1`}>
                        <View style={tw`row`}>
                          <Text style={tw`text-secondary font-500`}>{unit.name}</Text>
                          {
                            unit.street_name_2 &&
                              <Text style={tw`text-gray-400 ml-2`}>{unit.street_name_2}</Text>
                          }
                        </View>
                        {
                          unit.owners?.length > 0 &&
                            <Text style={tw`text-gray-400`}>{unit.owners[0].name}</Text>
                        }
                      </View>
                      <IcomoonIcon name='chevron-right' size={16} color={tw.color('secondary')} />
                    </TouchableOpacity>
                  )
            }
            {
              extraResultsCount > 0 &&
                <Text style={tw`text-gray-400 text-center text-sm py-3 border-t border-gray-100`}>
                  y {extraResultsCount} más… escribe para refinar
                </Text>
            }
          </View>
      }
    </View>
  )

  const pendientesSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 pt-5 pb-2 px-5 mb-4`}>
      <Text style={tw`text-secondary font-600 text-xl`}>Pendientes</Text>

      {
        totalPending === 0 &&
          <View style={tw`row items-center justify-center py-6`}>
            <View style={tw`bg-primary rounded-full w-7 h-7 items-center justify-center mr-2`}>
              <IcomoonIcon name='check' color={tw.color('white')} size={16} />
            </View>
            <Text style={tw`text-gray-600 font-500`}>Estás al día</Text>
          </View>
      }

      {
        totalPending > 0 &&
          <Text style={tw`text-gray-400 mb-4`}>Toca para ir a cada sección</Text>
      }

      {
        totalPending > 0 &&
        pendingItems.map(item =>
          <TouchableOpacity
            key={item.key}
            style={tw.style(`row justify-between rounded-xl px-3 py-3 mb-3`, item.highlight ? `bg-red/5 border border-[#FBD0DC]` : `bg-gray-50 border border-[#F0EBF7]`)}
            onPress={item.onPress}
          >
            <View style={tw`row flex-1`}>
              <View style={tw`w-11 h-11 rounded-xl items-center justify-center mr-3 bg-white`}>
                {item.icon}
              </View>
              <View style={tw`flex-1`}>
                <Text style={tw`text-secondary font-500`}>{item.title}</Text>
                {
                  item.subtitle &&
                    <Text style={tw`text-gray-400 text-sm`}>{item.subtitle}</Text>
                }
              </View>
            </View>
            <View style={tw`row`}>
              {
                item.count > 0 &&
                  <View style={tw`bg-red rounded-full h-5 min-w-[20px] px-1.5 items-center justify-center mr-2`}>
                    <Text style={tw`text-white text-xs font-600`}>{item.count}</Text>
                  </View>
              }
              <IcomoonIcon name='chevron-right' size={16} color={tw.color('gray-400')} />
            </View>
          </TouchableOpacity>
        )
      }
    </View>
  )

  const ingresosSection = (
    <View style={tw`bg-[#EAFBF9] border border-primary rounded-2xl pt-5 pb-4 px-5 mb-4`}>
      <View style={tw`row justify-between`}>
        <Text style={tw`text-secondary font-600 text-xl`}>Ingresos</Text>
        <TouchableOpacity style={tw`row`} onPress={() => navigation.navigate('Admin.Fees.Index')}>
          <Text style={tw`text-primary font-600`}>Pagos</Text>
          <IcomoonIcon name='chevron-right' size={14} color={tw.color('primary')} style={tw`ml-1`} />
        </TouchableOpacity>
      </View>
      <Text style={tw`text-gray-400 text-xs font-500 mt-4`}>CUOTA DE MANTENIMIENTO</Text>
      <Text style={tw`text-primary font-700 text-4xl`}>{money(income.maintenanceCollected)}</Text>
      <View style={tw`h-2 rounded-full bg-red/10 mt-3 overflow-hidden`}>
        <View style={tw.style(`h-full rounded-full bg-primary`, { width: `${incomeProgress}%` })} />
      </View>
      <Text style={tw`text-gray-400 text-sm mt-2`}>
        {income.maintenanceRemainingCount} pagos restantes ({money(income.maintenanceRemainingAmount)})
      </Text>
      <View style={tw`row justify-between mt-5`}>
        <View style={tw`row flex-1`}>
          <Icon name="cuota" size={30} style={tw`mr-3`} />
          <View>
            <Text style={tw`text-gray-800 font-600`}>Cuotas adicionales</Text>
            <Text style={tw`text-gray-400 text-sm`}>{income.extraordinaryCount} cuotas</Text>
          </View>
        </View>
        <Text style={tw`text-gray-800 font-700 text-xl self-center`}>{money(income.extraordinaryAmount)}</Text>
      </View>
      <IconButton title={"Registrar pago"} color='primary' textColor='white' style={"w-full mt-5"} onPress={() => setShowNewPaymentModal(true)} />
    </View>
  )

  const gastosSection = (
    <View style={tw`bg-[#FBF5FF] border border-secondary/20 rounded-2xl pt-5 pb-4 px-5 mb-4`}>
      <View style={tw`row justify-between`}>
        <Text style={tw`text-gray-800 font-600 text-xl`}>Gastos</Text>
        <TouchableOpacity style={tw`row`} onPress={() => navigation.navigate('Admin.Fees.Index')}>
          <Text style={tw`text-secondary font-600`}>Ver todo</Text>
          <IcomoonIcon name='chevron-right' size={14} color={tw.color('secondary')} style={tw`ml-1`} />
        </TouchableOpacity>
      </View>
      <Text style={tw`text-gray-400 text-xs font-500 mt-4`}>TOTAL DE GASTOS</Text>
      <Text style={tw`text-secondary font-700 text-4xl`}>{money(expenses.total)}</Text>
      <View style={tw`border-t border-secondary/10 my-4`} />
      <View style={tw`row`}>
        <View style={[tw`mr-3`, { transform: [{ rotate: '-90deg' }] }]}>
          <Icon name="rightArrowCircle" size={28} color={tw.color('secondary')} />
        </View>
        <Text style={tw`text-gray-800`}>
          <Text style={tw`font-700 text-xl`}>{expenses.count}</Text> pagos realizados
        </Text>
      </View>
      <IconButton title={"Registrar gasto"} color='secondary' textColor='white' style={"w-full mt-5"} onPress={() => setShowNewExpenseModal(true)} />
    </View>
  )

  const comunicacionSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 pt-5 pb-4 px-5 mb-4`}>
      <View style={tw`row justify-between`}>
        <Text style={tw`text-gray-800 font-600 text-xl`}>Comunicación</Text>
        <TouchableOpacity style={tw`row`} onPress={() => navigation.navigate('Admin.Announcements.Index', { communityId })}>
          <Text style={tw`text-primary font-600`}>Comunicados</Text>
          <IcomoonIcon name='chevron-right' size={14} color={tw.color('primary')} style={tw`ml-1`} />
        </TouchableOpacity>
      </View>
      {
        lastAnnouncement &&
          <TouchableOpacity
            style={tw`bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mt-3`}
            onPress={() => isWeb ? setDetailPanel({ type: 'announcement', data: lastAnnouncement }) : navigation.navigate('Admin.Announcement.Detail', { announcementId: lastAnnouncement.uuid })}
          >
            <Text style={tw`text-gray-400 text-xs font-500`}>ÚLTIMO AVISO · {relativeDateLabel(lastAnnouncement.created_at)}</Text>
            <Text style={tw`text-gray-800 font-600 mt-1`}>{lastAnnouncement.title}</Text>
            {
              hasReach &&
                <View style={tw`mt-2`}>
                  <View style={tw`row justify-between`}>
                    <Text style={tw`text-gray-400 text-sm`}>Visto por {reachSeen}/{reachTotal}</Text>
                    <Text style={tw`text-primary font-600 text-sm`}>{reachPct}%</Text>
                  </View>
                  <View style={tw`h-2 rounded-full bg-gray-200 mt-1 overflow-hidden`}>
                    <View style={tw.style(`h-full rounded-full bg-primary`, { width: `${reachPct}%` })} />
                  </View>
                </View>
            }
          </TouchableOpacity>
      }
      <TouchableOpacity
        style={tw`row justify-center border border-secondary rounded-full py-3 mt-4`}
        onPress={() => setShowNewAnnouncementModal(true)}
      >
        <IcomoonIcon name='add1' size={14} color={tw.color('secondary')} style={tw`mr-2`} />
        <Text style={tw`text-secondary font-600`}>Nuevo comunicado</Text>
      </TouchableOpacity>
      <View style={tw`border-t border-gray-100 my-5`} />
      <Text style={tw`text-gray-800 font-600 text-xl mb-3`}>Documentos</Text>
      <View style={tw`bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-3`}>
        <TouchableOpacity style={tw`row justify-between`} onPress={() => setDocsFolderOpen(o => !o)}>
          <Text style={tw`text-secondary font-600`}>{sampleFolder.name}</Text>
          <View style={tw`row`}>
            <Text style={tw`text-gray-400 mr-2`}>{sampleFolder.docs.length}</Text>
            <IcomoonIcon name='chevron-right' size={14} color={tw.color('secondary')} style={{ transform: [{ rotate: docsFolderOpen ? '-90deg' : '90deg' }] }} />
          </View>
        </TouchableOpacity>
        {
          docsFolderOpen &&
            <View style={tw`mt-2`}>
              {
                sampleFolder.docs.map((name, index) =>
                  <View key={name} style={tw.style(`row h-12`, index > 0 && `border-t border-gray-200`)}>
                    <View style={tw`bg-red rounded px-1 py-0.5 mr-3`}>
                      <Text style={tw`text-white text-[8px] font-700`}>PDF</Text>
                    </View>
                    <Text style={tw`text-gray-500 flex-1`} numberOfLines={1}>{name}</Text>
                  </View>
                )
              }
            </View>
        }
      </View>
      {
        data.documents?.map(doc =>
          <TouchableOpacity
            key={doc.uuid}
            style={tw`row bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-3`}
            onPress={() => setSelectedDocumentModal(doc)}
          >
            <View style={tw`bg-red rounded px-1 py-0.5 mr-3`}>
              <Text style={tw`text-white text-[8px] font-700`}>PDF</Text>
            </View>
            <Text style={tw`text-gray-500 flex-1`} numberOfLines={1}>{doc.name}</Text>
          </TouchableOpacity>
        )
      }
      <TouchableOpacity
        style={tw`row justify-center border border-secondary rounded-full py-3 mt-1`}
        onPress={() => setShowNewDocumentModal(true)}
      >
        <IcomoonIcon name='add1' size={14} color={tw.color('secondary')} style={tw`mr-2`} />
        <Text style={tw`text-secondary font-600`}>Nuevo documento</Text>
      </TouchableOpacity>
    </View>
  )

  // Columna 3: panel de detalle GENÉRICO del dashboard.
  // Hoy renderiza comunicados; a futuro se agregan más casos por `detailPanel.type`
  // (votaciones, reservaciones de zonas, etc.).
  const detailPanelSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 pt-5 pb-4 px-5 mb-4`}>
      {
        detailPanel?.type === 'announcement' ? (
          <>
            <View style={tw`row justify-between`}>
              <Text style={tw`text-gray-800 font-600 text-xl`}>Comunicado</Text>
              <TouchableOpacity onPress={() => setDetailPanel(null)}>
                <IcomoonIcon name='close' size={18} color={tw.color('gray-400')} />
              </TouchableOpacity>
            </View>
            <Text style={tw`text-gray-400 text-xs font-500 mt-3`}>{relativeDateLabel(detailPanel.data.created_at)}</Text>
            <Text style={tw`text-gray-800 font-700 text-lg mt-1`}>{detailPanel.data.title}</Text>
            <Text style={tw`text-gray-600 mt-3`}>{detailPanel.data.text}</Text>
          </>
        ) : (
          // TODO: más tipos de detalle aquí (votaciones, reservaciones, etc.)
          <View style={tw`items-center justify-center py-12`}>
            <Text style={tw`text-gray-400 text-center`}>Selecciona un elemento para ver su detalle aquí</Text>
          </View>
        )
      }
    </View>
  )

  return (
    <ScrollView
      style={tw`flex-1 bg-background`}
      contentContainerStyle={tw``}
      keyboardShouldPersistTaps='always'
      refreshControl={
        <RefreshControl onRefresh={refetch} refreshing={isLoading} />
      }
    >
      {/*
        Header bienvenida — solo en nativo.
        En web ya hay sidebar + topbar con el nombre del fraccionamiento, así que se omite
        para ir directo al contenido.
      */}
      {
        Platform.OS !== 'web' &&
          <Header style={tw`bg-secondary`}>
            <View style={tw`w-full`}>
              {/* Row Bienvenido */}
              <View style={tw`row`}>
                <View style={tw`rounded-lg mr-3 w-16 h-16 bg-white overflow-hidden`}>
                  <Image style={tw`w-full h-full`} source={data.community?.image_path} />
                </View>

                <View style={tw``}>
                  <Text style={tw`text-primary font-600 text-2xl`}>Bienvenido</Text>
                  <Text style={tw`text-white`}>{data.community?.name}</Text>
                </View>
              </View>

              {/*
                Fecha de hoy
              */}
              <Text style={tw`text-white font-500 mt-3`}>{todayLabel}</Text>
            </View>
          </Header>
      }


      <View style={tw`px-4 mt-2`}>

        <View onLayout={e => setContentWidth(e.nativeEvent.layout.width)}>
          {
            threeColumns ? (
              // Web muy ancho: tres columnas (la 3ª es el visor de comunicado)
              <View style={tw`flex-row`}>
                <View style={tw`flex-1 mr-2`}>
                  {buscadorSection}
                  {ingresosSection}
                  {gastosSection}
                </View>
                <View style={tw`flex-1 mx-2`}>
                  {pendientesSection}
                  {comunicacionSection}
                </View>
                <View style={tw`flex-1 ml-2`}>
                  {detailPanelSection}
                </View>
              </View>
            ) : multiColumn ? (
              // Web ancho: dos columnas (el visor cae en la columna 2 si hay comunicado abierto)
              <View style={tw`flex-row`}>
                <View style={tw`flex-1 mr-2`}>
                  {buscadorSection}
                  {ingresosSection}
                  {gastosSection}
                </View>
                <View style={tw`flex-1 ml-2`}>
                  {pendientesSection}
                  {comunicacionSection}
                  {detailPanel && detailPanelSection}
                </View>
              </View>
            ) : (
              // Angosto / nativo: una sola columna
              <>
                {buscadorSection}
                {pendientesSection}
                {ingresosSection}
                {gastosSection}
                {comunicacionSection}
                {detailPanel && detailPanelSection}
              </>
            )
          }
        </View>

        
      </View>

      <AddPaymentModal
        communityId={communityId}
        visible={showNewPaymentModal}
        onRequestClose={() => setShowNewPaymentModal(false)}
        onPaymentCreated={() => {
          setShowNewPaymentModal(false)
          refetch()
        }}
      />

      {
        showNewExpenseModal &&
          <NewExpenseModal
            communityId={communityId}
            visible
            onRequestClose={() => setShowNewExpenseModal(false)}
            onExpenseCreated={() => {
              setShowNewExpenseModal(false)
              refetch()
            }}
          />
      }

      <NewAnnouncementModal
        communityId={communityId}
        visible={showNewAnnouncementModal}
        onRequestClose={() => setShowNewAnnouncementModal(false)}
        onAnnouncementCreated={() => {
          setShowNewAnnouncementModal(false)
          refetch()
        }}
      />


      <NewDocumentModal
        communityId={communityId}
        visible={showNewDocumentModal}
        onRequestClose={() => setShowNewDocumentModal(false)}
        onDocumentCreated={() => {
          setShowNewDocumentModal(false)
          refetch()
        }}
      />

      {
        selectedDocumentModal &&
          <DocumentDetailModal
            document={selectedDocumentModal}
            onRequestClose={() => setSelectedDocumentModal(null)}
          />
      }
    </ScrollView>
  );
}
